import { ActionIcon, Divider, Group } from "@mantine/core";
import { ChevronDown } from "lucide-react";
import {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  type DragEventHandler,
  type ReactNode,
} from "react";

import classes from "./GlassPanel.module.css";

export interface GlassPanelProps {
  /** Left of the header row: a mode switch, a summary of the draft. */
  header?: ReactNode;
  /** What the collapsed panel shows instead of its body. */
  summary?: ReactNode;
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  /** The body: the prompt. */
  children: ReactNode;
  /** Under a dashed divider: the setting pills, the price and the start. */
  footer?: ReactNode;
  /** A pill toolbar floating just above the panel (the video composer's actions). */
  above?: ReactNode;
  /** A layer over the panel, from its bottom up (the video composer's Actions panel). */
  overlay?: ReactNode;
  /**
   * A glass card beside the panel's right edge, bottom-aligned (live's advanced settings, Voice
   * Settings), moved left as far as it must to stay inside the window, as live's sits.
   */
  aside?: ReactNode;
  /** A layer over the panel's whole face, the panel's shape (live's "Create Element"). */
  cover?: ReactNode;
  /**
   * In the page's flow, inside a card of its own (the Pro Studio redesign's Create pages, spec
   * ui-perf/pro-studio-redesign): no float at the page's bottom, no glass or collapse, the toolbar
   * above it wrapping, the aside under it. The Studio's floating panel is the default.
   */
  inline?: boolean | undefined;
  /** Files dropped anywhere on the panel (live's image composer is one drop area). */
  drop?: {
    onDragOver: DragEventHandler<HTMLElement>;
    onDragLeave: DragEventHandler<HTMLElement>;
    onDrop: DragEventHandler<HTMLElement>;
    /** Files are over it: live's dashed rule and tint, with no text. */
    over: boolean;
  };
}

/**
 * Every GlassPanel under it is inline, as its `inline` prop says, unless that prop says otherwise:
 * a page that draws another area's forms in its flow (Simple's template forms, spec
 * ui-perf/simple-studio) without each form taking the prop.
 */
export const InlinePanels = createContext(false);

/** The room the aside keeps from the window's right edge. */
const ASIDE_GUTTER = 16;

/**
 * The floating generation panel (design-system.md 3.3): 795px of frosted glass at the bottom
 * center of its page, 21px corners, sliding up as it appears; a focused field inside turns its
 * border primary. Collapsed, it is a 55px bar with a summary.
 */
export function GlassPanel(props: GlassPanelProps) {
  const { header, summary, collapsed, onCollapsedChange, children, footer, above, overlay, aside } =
    props;
  const inlined = useContext(InlinePanels);
  const { drop, cover, inline = inlined } = props;
  const asideRef = useRef<HTMLElement>(null);
  const open = aside !== undefined && aside !== null && !collapsed;
  // Beside the panel where the window has room; else moved left, over the panel's edge, so its
  // close button and list stay in the window and the page never scrolls sideways.
  useLayoutEffect(() => {
    const card = asideRef.current;
    if (!open || card === null) return;
    const place = () => {
      card.style.translate = "";
      const over = card.getBoundingClientRect().right - (window.innerWidth - ASIDE_GUTTER);
      card.style.translate = over > 0 ? `${-Math.ceil(over)}px 0` : "";
    };
    place();
    window.addEventListener("resize", place);
    return () => window.removeEventListener("resize", place);
  }, [open]);
  return (
    <div className={classes.float} data-inline={inline || undefined}>
      {above === undefined ? null : <div className={classes.above}>{above}</div>}
      <section
        className={classes.panel}
        data-collapsed={collapsed || undefined}
        data-drop-over={drop?.over || undefined}
        onDragOver={drop?.onDragOver}
        onDragLeave={drop?.onDragLeave}
        onDrop={drop?.onDrop}
      >
        <Group justify="space-between" wrap="nowrap" gap={20} className={classes.header}>
          <Group gap={20} wrap="nowrap" className={classes.headerContent}>
            {collapsed ? summary : header}
          </Group>
          {/* A card in the page's flow has nothing to collapse. */}
          {inline ? null : (
            <ActionIcon
              variant="subtle"
              size="lg"
              aria-label={collapsed ? "Expand the panel" : "Collapse the panel"}
              aria-expanded={!collapsed}
              onClick={() => onCollapsedChange(!collapsed)}
            >
              <ChevronDown size={20} className={classes.chevron} />
            </ActionIcon>
          )}
        </Group>
        {cover === undefined || cover === null || collapsed ? null : (
          <div className={classes.cover}>{cover}</div>
        )}
        {collapsed ? null : (
          <>
            {children}
            {footer === undefined ? null : (
              <>
                <Divider variant="dashed" my={12} />
                <div className={classes.footer}>{footer}</div>
              </>
            )}
          </>
        )}
      </section>
      {open ? (
        <aside ref={asideRef} className={classes.aside}>
          {aside}
        </aside>
      ) : null}
      {overlay === undefined || overlay === null ? null : (
        <div className={classes.overlay}>{overlay}</div>
      )}
    </div>
  );
}
